import React from 'react';
import { Sparkles, Share2, Mic } from 'lucide-react';
import { SoulEntry } from '../../types';

interface StudioSectionHeaderProps {
  activeEntry: SoulEntry | null;
  shareSuccessUrl?: string | null;
  onForkEntry?: () => void;
  onShareEntry: () => void;
  onDismissShareSuccess?: () => void;
  audioInputDevices?: MediaDeviceInfo[];
  selectedMicDeviceId?: string;
  onSelectMicDevice?: (deviceId: string) => void;
}

export const StudioSectionHeader: React.FC<StudioSectionHeaderProps> = ({
  activeEntry,
  shareSuccessUrl,
  onShareEntry,
  onDismissShareSuccess,
  audioInputDevices = [],
  selectedMicDeviceId = 'default',
  onSelectMicDevice
}) => {
  return (
    <>
      <div className="studio-section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={18} color="var(--accent-purple)" />
          <h1 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
            SoulMixing Engine
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Microphone Input Selector */}
          {audioInputDevices.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid var(--border-glass)',
                borderRadius: 'var(--radius-full)',
                padding: '3px 10px',
                fontSize: '0.74rem',
                color: '#cbd5e1'
              }}
              title="Active recording microphone (switches automatically on OS change or choose a specific device)"
            >
              <Mic size={12} color="var(--accent-purple)" />
              <select
                value={selectedMicDeviceId || 'default'}
                onChange={(e) => onSelectMicDevice?.(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  fontSize: '0.74rem',
                  outline: 'none',
                  cursor: 'pointer',
                  maxWidth: '180px',
                  textOverflow: 'ellipsis'
                }}
              >
                <option value="default" style={{ background: '#0f172a', color: '#f8fafc' }}>
                  Default (System Input)
                </option>
                {audioInputDevices.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId} style={{ background: '#0f172a', color: '#f8fafc' }}>
                    {d.label || `Microphone ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            onClick={onShareEntry}
            className="btn-primary"
            style={{
              fontSize: '0.74rem',
              padding: '4px 12px',
              background: activeEntry?.colabSharedClipId
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              boxShadow: activeEntry?.colabSharedClipId
                ? '0 2px 8px rgba(245, 158, 11, 0.4)'
                : '0 2px 8px rgba(168, 85, 247, 0.3)',
              cursor: 'pointer',
              color: '#ffffff',
              fontWeight: 700
            }}
            title={activeEntry?.colabSharedClipId
              ? `Send this duet session back to @${activeEntry.partnerName || 'Partner'}`
              : "Share this shot to SoulShare (Opens in Lightbox)"}
          >
            <Share2 size={13} />
            <span>
              {activeEntry?.colabSharedClipId
                ? `Send Back to @${activeEntry.partnerName || 'Partner'}`
                : 'Share'}
            </span>
          </button>
        </div>
      </div>
    </>
  );
};
